// angular
import { Component, effect, inject, input, OnChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { AdditionalSpecificationBoxComponent } from '@app/core/components/additional-specification-box/additional-specification-box.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
// interfaces & types
import { AdditionalSpecificationType } from '@app/+products/shared/interfaces/additional-specification.interface';
import { AdditionalProperty } from '@app/+products/shared/types/additional-property.type';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';

@Component({
  selector: 'pwj-product-additional-properties',
  standalone: true,
  imports: [
    AdditionalSpecificationBoxComponent,
    TranslateModule,
    TranslateDataPipe,
    ButtonComponent,
  ],
  templateUrl: './product-additional-properties.component.html',
  styleUrl: './product-additional-properties.component.scss',
})
export class ProductAdditionalPropertiesComponent {
  public properties!: AdditionalProperty[];
  public showAdditionalProperties: boolean = true;

  // inputs
  additionalProperties = input.required<AdditionalSpecificationType>();

  // dependencies
  private readonly _translateService = inject(TranslateService);

  constructor() {
    effect(() => {
      this.setProperties();
    });

    // Update properties on lang change
    this._translateService.onLangChange.subscribe(_ => {
      this.setProperties();
    });
  }

  private hasValue(value: unknown): boolean {
    if (value == null) return false;
    if (typeof value === 'string') return value !== '' && value !== '-';
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }

  public setProperties() {
    // set additionalProperties data – only include properties with non-null values
    const ap = this.additionalProperties();
    const raw = [
      {
        title: this._translateService.instant('products.companyResponsible'),
        value: ap?.company_responsible ?? null,
      },
      {
        title: this._translateService.instant('products.warranty'),
        value: ap?.warranty
          ? `${ap.warranty} ${this._translateService.instant('products.month')}`
          : null,
      },
      {
        title: 'products.monthlyProduction',
        value: ap?.production_capacity?.value
          ? `${ap.production_capacity.value} ${this._translateService.instant(
              `enums.measurements.${ap.production_capacity.unit}`
            )}`
          : null,
      },
      {
        title: 'products.mainColor',
        colorsList: ap?.main_color
          ? [
              {
                name: this._translateService.instant(
                  `enums.colors.${ap.main_color}`
                ),
                color: ap.main_color,
              },
            ]
          : undefined,
        colorPercentage: ap?.color_percentage,
      },
      {
        title: 'products.vein',
        colorsList: ap?.veins?.length
          ? ap.veins.map((value: string) => ({
              name: value
                ? this._translateService.instant(`enums.colors.${value}`)
                : '-',
              color: value,
            }))
          : undefined,
      },
      {
        title: 'products.point',
        colorsList: ap?.dots?.length
          ? ap.dots.map((value: string) => ({
              name: value
                ? this._translateService.instant(`enums.colors.${value}`)
                : '-',
              color: value,
            }))
          : undefined,
      },
      {
        title: 'products.halo',
        colorsList: ap?.halos?.length
          ? ap.halos.map((value: string) => ({
              name: value
                ? this._translateService.instant(`enums.colors.${value}`)
                : '-',
              color: value,
            }))
          : undefined,
      },
      {
        title: 'products.theNumberOfCuttingFaces',
        value: ap?.cutting_face ?? null,
      },
      {
        title: 'products.minOrder',
        value: ap?.min_order?.value
          ? `${ap.min_order.value} ${this._translateService.instant(
              `enums.measurements.${ap.min_order.unit}`
            )}`
          : null,
      },
      {
        title: 'products.shipmentTerms',
        value:
          (ap?.shipment_terms?.length || 0) > 0
            ? (ap.shipment_terms ?? []).join(' , ')
            : null,
      },
      {
        title: 'products.paymentMethods',
        value:
          (ap?.payment_methods?.length || 0) > 0
            ? (ap.payment_methods ?? []).join(' , ')
            : null,
      },
      {
        title: 'products.payment',
        value:
          ap?.payment_deposit && ap?.payment_balance
            ? `${ap.payment_deposit}%  ${this._translateService.instant(
                'products.depositBeforeProduction'
              )}  ${ap.payment_balance}%  ${this._translateService.instant(
                'products.balanceBeforeShipping'
              )}`
            : null,
      },
      {
        title: 'products.availableAmount',
        value: ap?.available_amount?.value
          ? `${ap.available_amount.value} ${this._translateService.instant(
              `enums.measurements.${ap.available_amount.unit}`
            )}`
          : null,
      },
      {
        title: 'products.address',
        value:
          typeof ap?.delivery_address === 'string'
            ? ap.delivery_address
            : (ap?.delivery_address as any)?.en ?? null,
      },
      {
        title: 'products.deliveryType',
        value: ap?.delivery_type
          ? this._translateService.instant(
              `enums.deliveryType.${ap.delivery_type}`
            )
          : null,
      },
      {
        title: 'products.size',
        titleSuffix: ap?.customizeSize ? 'products.custom' : null,
        sizeList:
          (ap?.dimensions?.length || 0) > 0
            ? ap.dimensions!.map(size => ({
                length: {
                  title: 'products.length',
                  unit: size?.length?.unit || '-',
                  value: size?.length?.value || '-',
                },
                length_tolerance: {
                  title: 'products.toleranceLength',
                  unit: size?.length_tolerance?.unit || '-',
                  value: size?.length_tolerance?.value || '-',
                },
                width: {
                  title: 'products.width',
                  unit: size?.width?.unit || '-',
                  value: size?.width?.value || '-',
                },
                width_tolerance: {
                  title: 'products.toleranceWidth',
                  unit: size?.width_tolerance?.unit || '-',
                  value: size?.width_tolerance?.value || '-',
                },
                height: {
                  title: 'products.height',
                  unit: size?.height?.unit || '-',
                  value: size?.height?.value || '-',
                },
                height_tolerance: {
                  title: 'products.toleranceHeight',
                  unit: size?.height_tolerance?.unit || '-',
                  value: size?.height_tolerance?.value || '-',
                },
                weight: {
                  title: 'products.weight',
                  unit: size?.weight?.unit || '-',
                  value: size?.weight?.value || '-',
                },
                weight_tolerance: {
                  title: 'products.toleranceWeight',
                  unit: size?.weight_tolerance?.unit || '-',
                  value: size?.weight_tolerance?.value || '-',
                },
                thickness: {
                  title: 'products.thickness',
                  unit: size?.thickness?.unit || '-',
                  value: size?.thickness?.value || '-',
                },
              }))
            : undefined,
        isEmpty:
          !ap?.dimensions || (ap.dimensions?.length || 0) < 1,
      },
    ];
    this.properties = raw.filter(
      p =>
        this.hasValue(p.value) ||
        (p.colorsList && p.colorsList.length > 0) ||
        (p.sizeList && p.sizeList.length > 0 && !p.isEmpty)
    );
  }

  public setColorBoxStyle(color?: string) {
    return `background: ${color};`;
  }

  public toggleAdditionalProperties() {
    this.showAdditionalProperties = !this.showAdditionalProperties;
  }
}
